import * as prismic from '@prismicio/client';
import * as prismicNext from '@prismicio/next';

import { PRODUCTION } from '@/utils';

/**
 * The project's Prismic repository name.
 *
 * Required — must be set via `NEXT_PUBLIC_PRISMIC_ENVIRONMENT`. Fails loudly
 * at import time when missing so a misconfigured environment can't silently
 * hit the wrong Prismic repo. Set it in `.env` (see `.env.example`).
 */
export const repositoryName = (() => {
  const value = process.env.NEXT_PUBLIC_PRISMIC_ENVIRONMENT;
  if (!value) {
    throw new Error(
      'NEXT_PUBLIC_PRISMIC_ENVIRONMENT is not set. Add it to your environment (see .env.example) — it is the Prismic repository name and there is no safe default.',
    );
  }
  return value;
})();

/**
 * A list of Route Resolver objects that define how a document's `url` field is resolved.
 *
 * {@link https://prismic.io/docs/route-resolver#route-resolver}
 */
const routes: prismic.ClientConfig['routes'] = [
  {
    type: 'page',
    uid: 'home',
    path: '/',
  },
  {
    type: 'page',
    path: '/:parent?/:uid',
    resolvers: {
      parent: 'parent_page',
    },
  },
  {
    type: 'resources_hub',
    path: '/resources-center',
  },
  {
    type: 'blog',
    path: '/resources-center/:uid',
  },
  {
    type: 'webinar_post',
    path: '/resources-center/webinar/:uid',
  },
  {
    type: 'case_study',
    path: '/clients/:uid',
  },
  {
    type: 'ebook',
    path: '/ebooks/:uid',
  },
  // Landings route to /resources-center/:category-uid/:uid. Prismic resolver
  // has no conditional-by-field-value support, so `legacy`-category landings
  // resolve to /resources-center/legacy/:uid too — the render handler at that
  // path 301s to the canonical /landing/:uid, keeping preview URLs alive and
  // legacy SEO stable.
  //
  // Requires every landing to have `category` linked — the client's resolver
  // validation throws on empty links (breaking ALL fetches, not just landing
  // ones). Content team back-filled every published landing with
  // `category = legacy`, so this is safe.
  {
    type: 'landing',
    path: '/resources-center/:category/:uid',
    resolvers: { category: 'category' },
  },
  {
    type: 'gated_form',
    path: '/gated/:uid',
  },
  {
    type: 'get_started',
    uid: 'get-started',
    path: '/get-started',
  },
  {
    type: 'get_started',
    path: '/get-started/:uid',
  },
];

/**
 * Creates a Prismic client for the project's repository. The client is used to
 * query content from the Prismic API.
 *
 * @param configParam
 */
export const createClient = (
  configParam: prismicNext.CreateClientConfig = {},
) => {
  const client = prismic.createClient(repositoryName, {
    routes,
    fetchOptions:
      process.env.NODE_ENV === PRODUCTION
        ? { next: { revalidate: 300 } }
        : { next: { revalidate: 5 } },
    // Reduce page size to prevent cache size issues
    defaultParams: {
      pageSize: 20,
    },
    // The `...configParam` spread is important. If `configParam.previewData` exists
    // and contains a `ref`, that `ref` will be passed to `prismic.createClient`,
    // making the client instance "preview-aware".
    ...configParam,
  });

  prismicNext.enableAutoPreviews({
    client,
    // previewData: configParam.previewData, // REMOVED: This line caused the type error.
    // The client is already preview-aware if configParam.previewData.ref was passed above.
    // req: configParam.req,
  });

  return client;
};
